import { Tooltip as AntTooltip } from "antd";
import { CheckCircle2, XCircle, Star } from "lucide-react";
import type { SeoPageEntry } from "@/services/wpSeoHealthService";

export interface SeoHealthTableProps {
	pages: SeoPageEntry[];
}

export function SeoHealthTable({ pages }: SeoHealthTableProps) {
	return (
		<div className="overflow-x-auto">
			<table className="w-full text-sm">
				<thead>
					<tr className="border-b bg-muted text-left text-xs font-medium text-muted-foreground">
						<th className="py-3 pl-5 pr-3">Page</th>
						<th className="py-3 pr-3 text-center"><AntTooltip title="Yoast SEO score: Green (71+), Orange (41-70), Red (0-40)"><span>SEO</span></AntTooltip></th>
						<th className="py-3 pr-3 text-center"><AntTooltip title="Yoast readability score"><span>Readability</span></AntTooltip></th>
						<th className="py-3 pr-3"><AntTooltip title="The main keyword this page is trying to rank for"><span>Focus Keyword</span></AntTooltip></th>
						<th className="py-3 pr-3"><AntTooltip title="Schema markup types from Schema Pro"><span>Schema</span></AntTooltip></th>
						<th className="py-3 pr-5 text-center"><AntTooltip title="Has a meta description set?"><span>Meta</span></AntTooltip></th>
					</tr>
				</thead>
				<tbody>
					{pages.map((page) => {
						const seoColor = page.seo_score >= 71 ? "bg-[hsl(var(--status-success))]" : page.seo_score >= 41 ? "bg-muted-foreground" : "bg-destructive";
						const readColor = page.readability_score >= 71 ? "bg-[hsl(var(--status-success))]" : page.readability_score >= 41 ? "bg-muted-foreground" : "bg-destructive";
						const seoTextColor = page.seo_score >= 71 ? "text-[hsl(var(--status-success))]" : page.seo_score >= 41 ? "text-muted-foreground" : "text-destructive";
						const readTextColor = page.readability_score >= 71 ? "text-[hsl(var(--status-success))]" : page.readability_score >= 41 ? "text-muted-foreground" : "text-destructive";
						const schemas = page.schema_pro_schemas ?? [];
						return (
							<tr key={page.url} className="border-b border-border transition-colors hover:bg-muted">
								<td className="max-w-[300px] py-3 pl-5 pr-3">
									<div className="min-w-0">
										<p className="truncate font-medium">
											{page.is_cornerstone && <Star className="mr-1 inline h-3.5 w-3.5 text-amber" />}
											{page.title}
										</p>
										<p className="truncate text-xs text-muted-foreground">
											{page.url}
											{page.robots_noindex && <span className="ml-1.5 rounded bg-muted px-1 py-0.5 text-[10px] font-medium text-muted-foreground">NOINDEX</span>}
										</p>
									</div>
								</td>
								<td className="py-3 pr-3 text-center">
									<div className="inline-flex flex-col items-center">
										<span className={`text-sm font-bold ${seoTextColor}`}>{page.seo_score}</span>
										<div className="mt-0.5 h-1.5 w-10 overflow-hidden rounded-full bg-muted">
											<div className={`h-full rounded-full ${seoColor}`} style={{ width: `${page.seo_score}%` }} />
										</div>
									</div>
								</td>
								<td className="py-3 pr-3 text-center">
									<div className="inline-flex flex-col items-center">
										<span className={`text-sm font-bold ${readTextColor}`}>{page.readability_score}</span>
										<div className="mt-0.5 h-1.5 w-10 overflow-hidden rounded-full bg-muted">
											<div className={`h-full rounded-full ${readColor}`} style={{ width: `${page.readability_score}%` }} />
										</div>
									</div>
								</td>
								<td className="py-3 pr-3">
									{page.focus_keyword ? <span className="text-sm text-foreground">{page.focus_keyword}</span> : <span className="text-xs text-muted-foreground/60 italic">not set</span>}
								</td>
								<td className="py-3 pr-3">
									{schemas.length > 0 ? (
										<div className="flex flex-wrap gap-1">
											{schemas.map((s) => <span key={s} className="rounded bg-[hsl(var(--status-success-bg))] px-1.5 py-0.5 text-[11px] font-medium text-[hsl(var(--status-success))]">{s}</span>)}
										</div>
									) : <span className="text-xs text-destructive">None</span>}
								</td>
								<td className="py-3 pr-5 text-center">
									{page.meta_description ? <CheckCircle2 className="mx-auto h-4 w-4 text-[hsl(var(--status-success))]" /> : <XCircle className="mx-auto h-4 w-4 text-destructive" />}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}
